/**
 * Simulated Labor endpoints Part 7 needs that the demo API router does not
 * answer yet: `/api/qb-labor` (the lunch + service-item + top-jobs supplement),
 * `/api/qb-labor-day` (Daily Timesheet Review) and `/api/qb-attendance`.
 *
 * WHAT IS REAL: the roster and the shop/field signal come from
 * `time_clock_snapshots` (real employee names, real `qbtime_user_id`, real
 * `job_number` / `is_shop` / `lunch_taken` / `lunch_minutes` / `is_pto`) and
 * `profiles`. Any day the snapshot actually covers uses its real segments.
 *
 * WHAT IS GENERATED, and why: `profiles` in this snapshot has NO `hire_date`
 * and NO `pay_rate` column, and `time_clock_snapshots` holds 30 rows — far less
 * than a year of attendance. Per spec §4 an employee with no hire date and no
 * pay rate is returned flagged `salaried: true` with null metrics; applying that
 * literally would empty the whole tab, so the people who appear on the clock get
 * a deterministic hire date + pay rate and are treated as established, while
 * profile-only staff stay `salaried: true` exactly as the spec says. Both facts
 * are stated in `demo_note` and badged on screen.
 */
import { loadTable } from '~/demo/data';
import { once, seededFloat, seededInt, seededPick } from '~/demo/simulated/prng';
import { nyDate, num, round2, str } from '~/demo/simulated/finance';
import {
  attendanceWindow, classifyAttendanceDay, isEstablishedEmployee, isoDay, ptoCarryover,
  splitRegularOvertime, startOfWorkWeek, workdaysBetween, type AttendanceDay,
} from '../rules/labor';

/** Spec §4 — company holidays the attendance calendar skips. */
export const COMPANY_HOLIDAYS: readonly string[] = Object.freeze([
  '01-01', '05-25', '07-04', '09-01', '11-26', '11-27', '12-24', '12-25',
]);

const isHoliday = (day: string): boolean => COMPANY_HOLIDAYS.includes(day.slice(5));

export interface LaborPerson {
  name: string;
  qbtimeUserId: string;
  /** Deterministic stand-in — `profiles` has no `pay_rate` column (see header). */
  payRate: number;
  /** Deterministic stand-in — `profiles` has no `hire_date` column (see header). */
  hireDate: string | null;
  /** True when this person actually appears in `time_clock_snapshots`. */
  onTheClock: boolean;
  shop: boolean;
}

/** The roster, built from `time_clock_snapshots` first and `profiles` second. */
export const laborRoster = once(async (): Promise<LaborPerson[]> => {
  const [snaps, profiles] = await Promise.all([
    loadTable('time_clock_snapshots'), loadTable('profiles'),
  ]);

  const byName = new Map<string, LaborPerson>();
  for (const r of snaps) {
    const name = str(r.employee_name).replace(/\s*\(\d+\)\s*$/, '').trim();
    if (!name) continue;
    const shop = r.is_shop === true || !str(r.job_number);
    const cur = byName.get(name);
    byName.set(name, {
      name,
      qbtimeUserId: str(r.qbtime_user_id) || (cur?.qbtimeUserId ?? ''),
      payRate: round2(seededFloat(`rate|${name}`, 24, 44)),
      hireDate: `${2026 - seededInt(`tenure|${name}`, 1, 7)}-${String(seededInt(`hm|${name}`, 1, 12)).padStart(2, '0')}-${String(seededInt(`hd|${name}`, 1, 28)).padStart(2, '0')}`,
      onTheClock: true,
      shop: (cur?.shop ?? false) || shop,
    });
  }
  for (const p of profiles) {
    if (p.is_active === false) continue;
    const name = str(p.display_name) || str(p.email);
    if (!name || byName.has(name)) continue;
    byName.set(name, {
      name,
      qbtimeUserId: '',
      // No pay_rate column on profiles → 0 → `isEstablishedEmployee` is false →
      // the spec's `salaried: true` branch, which is exactly what we want here.
      payRate: 0,
      hireDate: null,
      onTheClock: false,
      shop: true,
    });
  }
  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
});

/** Deterministic hours for one person on one day. Weekends are always zero. */
function dayHours(p: LaborPerson, day: string): { worked: number; pto: boolean } {
  const d = new Date(`${day}T12:00:00`);
  const dow = d.getDay();
  if (dow === 0 || dow === 6) return { worked: 0, pto: false };
  if (isHoliday(day)) return { worked: 0, pto: false };
  const roll = seededFloat(`${p.name}|${day}|roll`, 0, 1);
  if (roll < 0.04) return { worked: 0, pto: true };
  if (roll < 0.07) return { worked: 0, pto: false };
  return {
    worked: round2(p.shop ? seededFloat(`${p.name}|${day}|h`, 7.2, 9.0)
      : seededFloat(`${p.name}|${day}|h`, 7.6, 10.6)),
    pto: false,
  };
}

// ── /api/qb-labor — lunch, service items, top jobs ──────────────────────────

export interface ServiceItemRow { item: string; hours: number }
export interface LaborTopJob { jobNumber: string; hours: number }
export interface LaborExtrasPayload {
  ok: true;
  simulated: true;
  demo_note: string;
  periodStart: string;
  periodEnd: string;
  /** Unpaid lunch hours deducted across the period — weekly views only (spec §4). */
  lunchHours: number;
  /** Per-employee top 3 jobs by hours. */
  topJobs: Record<string, LaborTopJob[]>;
  /** Spec §4 — only populated on This week / Last week. */
  serviceItems: ServiceItemRow[];
}

const SERVICE_ITEMS = [
  'Water Extraction', 'Demolition', 'Drying / Monitoring', 'Contents Pack-Out',
  'Structural Cleaning', 'Mold Remediation', 'Reconstruction', 'Shop / Equipment Prep',
] as const;

/**
 * `/api/qb-labor` supplement: the three things `/api/qb-time` does not carry —
 * the unpaid-lunch deduction, the per-employee top jobs, and the Service Item
 * bar list (spec §4: available ONLY on This week / Last week, because the month
 * and YTD views use payroll totals that do not carry the custom field).
 */
export async function simulatedLaborExtras(
  start: Date, end: Date, weekly: boolean,
): Promise<LaborExtrasPayload> {
  const [people, snaps, invoices] = await Promise.all([
    laborRoster(), loadTable('time_clock_snapshots'), loadTable('job_invoices'),
  ]);
  const days = workdaysBetween(start, end);
  const jobNumbers = [...new Set(invoices.map((r) => str(r.job_number)).filter(Boolean))];

  // Real lunch minutes where the clock snapshot covers the window; otherwise the
  // standard unpaid 30 minutes per worked day.
  const inWindow = snaps.filter((r) => {
    const d = str(r.started_at || r.captured_at).slice(0, 10);
    return d >= isoDay(start) && d <= isoDay(end);
  });
  let lunchHours = 0;
  if (inWindow.length > 0) {
    for (const r of inWindow) lunchHours += num(r.lunch_minutes) / 60;
  }
  const topJobs: Record<string, LaborTopJob[]> = {};
  let workedDays = 0;
  const serviceHours = new Map<string, number>();

  for (const p of people) {
    if (!p.onTheClock) continue;
    const perJob = new Map<string, number>();
    for (const d of days) {
      const day = isoDay(d);
      const { worked } = dayHours(p, day);
      if (worked <= 0) continue;
      workedDays += 1;
      const jn = p.shop
        ? 'Shop'
        : (seededPick(`job|${p.name}|${day}`, jobNumbers) ?? 'Shop');
      perJob.set(jn, round2((perJob.get(jn) ?? 0) + worked));
      if (weekly) {
        const item = seededPick(`svc|${p.name}|${day}`, SERVICE_ITEMS) ?? SERVICE_ITEMS[0];
        serviceHours.set(item, round2((serviceHours.get(item) ?? 0) + worked));
      }
    }
    topJobs[p.name] = [...perJob.entries()]
      .map(([jobNumber, hours]) => ({ jobNumber, hours }))
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 3);
  }
  if (inWindow.length === 0) lunchHours = workedDays * 0.5;

  return {
    ok: true,
    simulated: true,
    demo_note:
      'Lunch uses real time_clock_snapshots.lunch_minutes where the window is covered, else the standard unpaid '
      + '30 min per worked day; top jobs and Service Items are generated per employee per day from a fixed seed '
      + '(the snapshot has no Service Item custom field).',
    periodStart: isoDay(start),
    periodEnd: isoDay(end),
    lunchHours: round2(lunchHours),
    topJobs,
    serviceItems: [...serviceHours.entries()]
      .map(([item, hours]) => ({ item, hours: round2(hours) }))
      .sort((a, b) => b.hours - a.hours),
  };
}

// ── /api/qb-labor-day — Daily Timesheet Review ──────────────────────────────

export interface TimesheetSegment {
  /** Minutes past midnight. */
  startMin: number;
  endMin: number;
  jobNumber: string | null;
  jobcode: string;
  kind: 'work' | 'lunch' | 'pto';
}
export interface TimesheetPerson {
  name: string;
  segments: TimesheetSegment[];
  totalHours: number;
}
export interface LaborDayPayload {
  ok: true;
  simulated: true;
  demo_note: string;
  date: string;
  /** Timeline bounds, minutes past midnight. */
  dayStartMin: number;
  dayEndMin: number;
  employees: TimesheetPerson[];
}

const minsOf = (iso: string): number => {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.getUTCHours() * 60 + d.getUTCMinutes() : 0;
};

/**
 * `/api/qb-labor-day` — every employee's raw clock-in/out segments on one date,
 * on a shared timeline so gaps and lunch are obvious. Uses the REAL
 * `time_clock_snapshots` rows for a date the snapshot covers; otherwise the
 * segments are generated from the same day-hours seed the rest of Labor uses.
 */
export async function simulatedLaborDay(date: string): Promise<LaborDayPayload> {
  const [people, snaps] = await Promise.all([laborRoster(), loadTable('time_clock_snapshots')]);
  const real = snaps.filter((r) => str(r.started_at || r.captured_at).slice(0, 10) === date);

  const employees: TimesheetPerson[] = [];
  if (real.length > 0) {
    const byName = new Map<string, TimesheetSegment[]>();
    for (const r of real) {
      const name = str(r.employee_name).replace(/\s*\(\d+\)\s*$/, '').trim();
      const startMin = minsOf(str(r.started_at));
      const mins = num(r.minutes_on_clock);
      const segs = byName.get(name) ?? [];
      segs.push({
        startMin,
        endMin: startMin + mins,
        jobNumber: str(r.job_number) || null,
        jobcode: str(r.jobcode_name) || 'Shop',
        kind: r.is_pto === true ? 'pto' : 'work',
      });
      if (r.lunch_taken === true && num(r.lunch_minutes) > 0) {
        segs.push({
          startMin: startMin + Math.round(mins / 2),
          endMin: startMin + Math.round(mins / 2) + num(r.lunch_minutes),
          jobNumber: null, jobcode: 'Lunch', kind: 'lunch',
        });
      }
      byName.set(name, segs);
    }
    for (const [name, segments] of byName) {
      employees.push({
        name,
        segments: segments.sort((a, b) => a.startMin - b.startMin),
        totalHours: round2(segments.filter((s) => s.kind === 'work')
          .reduce((s, x) => s + (x.endMin - x.startMin) / 60, 0)),
      });
    }
  } else {
    for (const p of people) {
      if (!p.onTheClock) continue;
      const { worked, pto } = dayHours(p, date);
      if (worked <= 0 && !pto) continue;
      const startMin = seededInt(`in|${p.name}|${date}`, 6 * 60 + 30, 8 * 60 + 30);
      if (pto) {
        employees.push({
          name: p.name,
          segments: [{ startMin: 8 * 60, endMin: 16 * 60, jobNumber: null, jobcode: 'PTO', kind: 'pto' }],
          totalHours: 0,
        });
        continue;
      }
      const half = Math.round((worked * 60) / 2);
      const lunchMin = 30;
      employees.push({
        name: p.name,
        segments: [
          { startMin, endMin: startMin + half, jobNumber: null, jobcode: p.shop ? 'Shop' : 'Field', kind: 'work' },
          { startMin: startMin + half, endMin: startMin + half + lunchMin, jobNumber: null, jobcode: 'Lunch', kind: 'lunch' },
          {
            startMin: startMin + half + lunchMin, endMin: startMin + Math.round(worked * 60) + lunchMin,
            jobNumber: null, jobcode: p.shop ? 'Shop' : 'Field', kind: 'work',
          },
        ],
        totalHours: worked,
      });
    }
  }

  employees.sort((a, b) => a.name.localeCompare(b.name));
  const allMins = employees.flatMap((e) => e.segments.flatMap((s) => [s.startMin, s.endMin]));
  return {
    ok: true,
    simulated: true,
    demo_note: real.length > 0
      ? `Segments for ${date} are the real time_clock_snapshots rows (clock-in time, minutes on clock, jobcode, lunch).`
      : `The snapshot has no clock rows for ${date}, so segments are generated per employee from a fixed seed.`,
    date,
    dayStartMin: allMins.length ? Math.max(0, Math.min(...allMins) - 30) : 6 * 60,
    dayEndMin: allMins.length ? Math.min(24 * 60, Math.max(...allMins) + 30) : 18 * 60,
    employees,
  };
}

// ── /api/qb-attendance ──────────────────────────────────────────────────────

export interface AttendancePerson {
  name: string;
  hireDate: string | null;
  /** Spec §4 — true when hire_date / pay_rate are missing; metrics are null. */
  salaried: boolean;
  scheduled: number | null;
  present: number | null;
  pto: number | null;
  approved: number | null;
  unapproved: number | null;
  attendancePct: number | null;
  ptoCarryoverHours: number;
  /** 'YYYY-MM-DD' → classification, for the calendar heatmap. */
  days: Record<string, AttendanceDay>;
}
export interface AttendancePayload {
  ok: true;
  simulated: true;
  demo_note: string;
  summary: {
    attendancePct: number | null;
    confirmedPtoDays: number;
    approvedAbsences: number;
    unapprovedAbsences: number;
    plannedPto: number;
  };
  people: AttendancePerson[];
  /** Spec §4 — the data-health note. */
  health: {
    calendarEvents: number;
    ptoCalendarErrors: number;
    /** Calendar events that matched no employee — they count for nobody. */
    unmatchedEvents: string[];
    missingFields: string[];
  };
}

/**
 * `/api/qb-attendance` — per Mon–Fri workday in `[max(hireDate, Jan 1) .. today]`
 * minus company holidays, classified present / pto / approved / unapproved /
 * holiday by `classifyAttendanceDay` (spec §4). Only ESTABLISHED employees are
 * scored; everyone else comes back `salaried: true` with null metrics.
 */
export const simulatedAttendance = once(async (): Promise<AttendancePayload> => {
  const people = await laborRoster();
  const today = new Date(`${nyDate()}T12:00:00`);

  const rows: AttendancePerson[] = [];
  const summary = { present: 0, scheduled: 0, pto: 0, approved: 0, unapproved: 0 };

  for (const p of people) {
    const established = isEstablishedEmployee({ hireDate: p.hireDate, payRate: p.payRate });
    if (!established) {
      rows.push({
        name: p.name, hireDate: p.hireDate, salaried: true,
        scheduled: null, present: null, pto: null, approved: null, unapproved: null,
        attendancePct: null, ptoCarryoverHours: ptoCarryover(p.name), days: {},
      });
      continue;
    }
    const { start, end } = attendanceWindow(p.hireDate, today);
    const days: Record<string, AttendanceDay> = {};
    let present = 0, pto = 0, approved = 0, unapproved = 0, scheduled = 0;
    for (const d of workdaysBetween(start, end, 300)) {
      const day = isoDay(d);
      const holiday = isHoliday(day);
      const { worked, pto: isPto } = dayHours(p, day);
      const roll = seededFloat(`${p.name}|${day}|cover`, 0, 1);
      const cls = classifyAttendanceDay({
        isHoliday: holiday,
        hasWorkedClockIn: worked > 0,
        onPtoCalendar: isPto && roll < 0.7,
        hasPtoJobcode: isPto && roll >= 0.7,
      });
      days[day] = cls;
      if (cls === 'holiday') continue;
      scheduled += 1;
      if (cls === 'present') present += 1;
      else if (cls === 'pto') pto += 1;
      else if (cls === 'approved') approved += 1;
      else unapproved += 1;
    }
    summary.present += present; summary.scheduled += scheduled;
    summary.pto += pto; summary.approved += approved; summary.unapproved += unapproved;
    rows.push({
      name: p.name, hireDate: p.hireDate, salaried: false,
      scheduled, present, pto, approved, unapproved,
      attendancePct: scheduled > 0 ? round2((present / scheduled) * 100) : null,
      ptoCarryoverHours: ptoCarryover(p.name),
      days,
    });
  }

  const salariedCount = rows.filter((r) => r.salaried).length;
  return {
    ok: true,
    simulated: true,
    demo_note:
      'Each Mon–Fri workday is classified by the spec rule (present / pto / approved / unapproved / holiday) over '
      + 'generated clock activity, because time_clock_snapshots holds 30 rows. `profiles` has no hire_date and no '
      + `pay_rate column, so the ${salariedCount} people who never appear on the clock come back salaried:true with `
      + 'null metrics, exactly as the spec requires, and the rest get a deterministic hire date + pay rate.',
    summary: {
      attendancePct: summary.scheduled > 0 ? round2((summary.present / summary.scheduled) * 100) : null,
      confirmedPtoDays: summary.pto,
      approvedAbsences: summary.approved,
      unapprovedAbsences: summary.unapproved,
      plannedPto: rows.reduce((s, r) => s + (r.salaried ? 0 : Math.round(r.ptoCarryoverHours / 8)), 0),
    },
    people: rows,
    health: {
      calendarEvents: rows.reduce((s, r) => s + (r.pto ?? 0), 0),
      ptoCalendarErrors: 0,
      // Spec §4: an event that matches no employee counts for NOBODY until it is
      // retitled with the person's name. None in the demo — the calendar is
      // generated from the roster itself.
      unmatchedEvents: [],
      missingFields: ['profiles.hire_date', 'profiles.pay_rate'],
    },
  };
});

/** Utility shared with the Labor tab: OT split for a set of day-hours. */
export function overtimeForDays(hours: { date: string; worked: number }[]): { regular: number; ot: number } {
  const byWeek = new Map<string, number>();
  for (const h of hours) {
    if (h.worked <= 0) continue;
    const wk = isoDay(startOfWorkWeek(new Date(`${h.date}T12:00:00`)));
    byWeek.set(wk, (byWeek.get(wk) ?? 0) + h.worked);
  }
  return splitRegularOvertime(byWeek.values());
}
